import React from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import ScadaCase from './cases/ScadaCase';
import TaskCase from './cases/TaskCase';
import InputCase from './cases/InputCase';
import DeviceCase from './cases/DeviceCase';

interface GeneralVariableSelectorRefactorProps {
  onVariableSelect: (
    variableId: number | null,
    variableName?: string | null,
    variableObj?: any | null
  ) => void;
  selectedVariableId?: number | null;
  disabled?: boolean;
  showHeader?: boolean;
  showPaper?: boolean;
  initialTab?: 'SCADA' | 'TAREAS' | 'INPUT' | 'DEVICE';
  selectedPlantId?: number | null;
  selectedZoneId?: number | null;
  selectedTableId?: number | null;
  onPlantChange?: (plant: any | null) => void;
  onZoneChange?: (zone: any | null) => void;
  onTableChange?: (table: any | null) => void;
}

export default function GeneralVariableSelectorRefactor({
  onVariableSelect,
  selectedVariableId,
  disabled = false,
  showHeader = true,
  showPaper = false,
  initialTab = 'SCADA',
  selectedPlantId,
  selectedZoneId,
  selectedTableId,
  onPlantChange,
  onZoneChange,
  onTableChange,
}: GeneralVariableSelectorRefactorProps) {
  const [selected, setSelected] = React.useState<
    'SCADA' | 'TAREAS' | 'ENTRADA' | 'DISPOSITIVOS'
  >(
    initialTab === 'INPUT'
      ? 'ENTRADA'
      : initialTab === 'DEVICE'
        ? 'DISPOSITIVOS'
        : (initialTab as any)
  );

  const [currentPlantId, setCurrentPlantId] = React.useState<number | null>(
    selectedPlantId || null
  );
  const [currentZoneId, setCurrentZoneId] = React.useState<number | null>(
    selectedZoneId || null
  );
  const [currentTableId, setCurrentTableId] = React.useState<number | null>(
    selectedTableId || null
  );
  const [currentVariableId, setCurrentVariableId] = React.useState<
    number | null
  >(selectedVariableId || null);

  React.useEffect(() => {
    setCurrentPlantId(selectedPlantId || null);
  }, [selectedPlantId]);

  React.useEffect(() => {
    setCurrentZoneId(selectedZoneId || null);
  }, [selectedZoneId]);

  React.useEffect(() => {
    setCurrentTableId(selectedTableId || null);
  }, [selectedTableId]);

  React.useEffect(() => {
    setCurrentVariableId(selectedVariableId || null);
  }, [selectedVariableId]);

  const handlePlantChange = (plantId: number | null) => {
    setCurrentPlantId(plantId);
    onPlantChange?.({ id: plantId } as any); // Adapt to Plant object
  };

  const handleZoneChange = (zoneId: number | null) => {
    setCurrentZoneId(zoneId);
    onZoneChange?.({ id: zoneId } as any); // Adapt to Zone object
  };

  const handleTableChange = (tableId: number | null) => {
    setCurrentTableId(tableId);
    onTableChange?.({ id: tableId } as any); // Adapt to Table object
  };

  const handleVariableChange = (
    variableId: number | null,
    variableName?: string | null,
    variableObj?: any | null
  ) => {
    setCurrentVariableId(variableId);
    onVariableSelect(variableId, variableName, variableObj);
  };

  const renderContent = () => {
    switch (selected) {
      case 'SCADA':
        return (
          <ScadaCase
            selectedPlantId={currentPlantId}
            selectedZoneId={currentZoneId}
            selectedTableId={currentTableId}
            selectedVariableId={currentVariableId}
            onPlantChange={handlePlantChange}
            onZoneChange={handleZoneChange}
            onTableChange={handleTableChange}
            onVariableChange={handleVariableChange}
          />
        );
      case 'TAREAS':
        return (
          <TaskCase
            selectedPlantId={currentPlantId}
            selectedZoneId={currentZoneId}
            selectedVariableId={currentVariableId}
            onPlantChange={handlePlantChange}
            onZoneChange={handleZoneChange}
            onVariableChange={handleVariableChange}
          />
        );
      case 'ENTRADA':
        return (
          <InputCase
            selectedPlantId={currentPlantId}
            selectedZoneId={currentZoneId}
            selectedTableId={currentTableId}
            selectedVariableId={currentVariableId}
            onPlantChange={handlePlantChange}
            onZoneChange={handleZoneChange}
            onTableChange={handleTableChange}
            onVariableChange={handleVariableChange}
          />
        );
      case 'DISPOSITIVOS':
        return (
          <DeviceCase
            selectedPlantId={currentPlantId}
            selectedVariableId={currentVariableId}
            onPlantChange={handlePlantChange}
            onVariableChange={handleVariableChange}
          />
        );
      default:
        return null;
    }
  };

  const content = (
    <Box width='100%'>
      {showHeader && (
        <>
          {/* Primera fila: SCADA | TAREAS */}
          <Box sx={{ p: 1 }}>
            <Box display='flex'>
              <Button
                variant={selected === 'SCADA' ? 'contained' : 'outlined'}
                size='large'
                fullWidth
                sx={{ borderRadius: 0, borderTopLeftRadius: 5 }}
                onClick={() => setSelected('SCADA')}
                disabled={disabled}
              >
                DATOS PROCESO
              </Button>
              <Button
                variant={selected === 'TAREAS' ? 'contained' : 'outlined'}
                size='large'
                fullWidth
                sx={{ borderRadius: 0, borderTopRightRadius: 5 }}
                onClick={() => setSelected('TAREAS')}
                disabled={disabled}
              >
                TAREAS
              </Button>
            </Box>

            {/* Segunda fila: ENTRADA | DISPOSITIVOS */}
            <Box display='flex'>
              <Button
                variant={selected === 'ENTRADA' ? 'contained' : 'outlined'}
                size='large'
                fullWidth
                sx={{ borderRadius: 0, borderBottomLeftRadius: 5 }}
                onClick={() => setSelected('ENTRADA')}
                disabled={disabled}
              >
                DATOS ENTRADA
              </Button>
              <Button
                variant={selected === 'DISPOSITIVOS' ? 'contained' : 'outlined'}
                size='large'
                fullWidth
                sx={{ borderRadius: 0, borderBottomRightRadius: 5 }}
                onClick={() => setSelected('DISPOSITIVOS')}
                disabled={disabled}
              >
                DISPOSITIVOS
              </Button>
            </Box>
          </Box>
        </>
      )}

      {/* Área de contenido */}
      <Box mt={1} p={1} borderRadius={1} minHeight={36}>
        {renderContent()}
      </Box>
    </Box>
  );

  if (showPaper) {
    return (
      <Paper
        sx={{
          p: 2,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 2,
          boxShadow: t =>
            t.palette.mode === 'dark'
              ? '0 4px 20px rgba(0,0,0,0.25)'
              : '0 4px 20px rgba(0,0,0,0.08)',
        }}
      >
        {content}
      </Paper>
    );
  }

  return content;
}
